import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import IgnisIcon from '../components/icons/IgnisIcon';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useServedModel } from '../hooks/useServedModel';
import { IGNIS_MODELS, STATE_DATE, fmtInt, fmtPct1 } from '../lib/siteState';

function Sub({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="font-display text-lg font-bold text-mist">{title}</h3>
      <div className="mt-2 space-y-3 leading-relaxed text-mist-muted">{children}</div>
    </div>
  );
}

const FEATURE_FAMILIES: [string, string][] = [
  ['Meteorologia e reanálise', 'Copernicus ERA5-Land, CERRA (vento de alta resolução), IPMA'],
  ['Índices de perigo (sistema FWI)', 'FFMC, DMC, DC, ISI, BUI, FWI, défice de pressão de vapor'],
  ['Terreno', 'elevação, declive, exposição, curvatura, TPI (Copernicus DEM), microtopografia'],
  ['Ocupação do solo', 'CORINE, densidade florestal, altura do coberto'],
  ['Vegetação (satélite)', 'NDVI, NDMI, LAI, FAPAR (contributo limitado, ver Resultados)'],
  ['Histórico de fogos', 'ICNF (ignições e densidade), Atlas de severidade dNBR 1984-2022'],
  ['Temperatura de superfície', 'MODIS MOD11A1 (LST)'],
  ['Pressão humana', 'população, interface urbano-florestal, infraestruturas'],
];

export default function Ignis() {
  useDocumentMeta(
    'IGNIS: previsão de risco de incêndio para Portugal | Tutela',
    'Como o IGNIS prevê o risco de incêndio rural em Portugal: dados, modelo de machine learning, resolução de 1 km e avaliação honesta. Complemento ao IPMA e ao EFFIS.',
    '/ignis',
  );
  // Tense keys on the served model family (see useServedModel): the rebuilt
  // model is "em sombra" until the switch, "em serviço" after it.
  const { rebuiltServed } = useServedModel();
  const cov = IGNIS_MODELS.rebuilt.coverage2026;
  const sh = IGNIS_MODELS.rebuilt.shadow;
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
      <div className="mx-auto max-w-3xl px-6 py-20">
        {/* Header */}
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-ember/30 bg-ember/10 px-4 py-1.5 font-mono text-sm font-semibold text-ember">
          <IgnisIcon className="h-4 w-4" />
          Módulo IGNIS
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-mist sm:text-4xl">
          Previsão de risco de incêndio para Portugal
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-mist-muted">
          O IGNIS estima, todos os dias, o risco de incêndio rural para cada quadrícula de 1 km de
          Portugal continental, com 24 a 72 horas de antecedência.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/ignis/mapa-nacional"
            className="inline-flex items-center gap-2 rounded-lg bg-ember px-6 py-3 font-semibold text-ink-950 shadow-[0_0_30px_-8px_#F26A4B] transition-all duration-200 hover:brightness-110"
          >
            Ver o mapa nacional
          </Link>
          <Link
            to="/ignis/modelos"
            className="inline-flex items-center gap-2 rounded-lg border border-ink-700 px-6 py-3 font-medium text-mist transition-colors hover:bg-ink-800"
          >
            Os modelos
          </Link>
        </div>

        {/* ============================ PART 1 ============================ */}
        <div className="mt-16 border-t border-ink-800 pt-12">
          <div className="mb-2 font-mono text-xs uppercase tracking-[0.22em] text-ember">
            Em linguagem simples
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-mist sm:text-3xl">
            O que é o IGNIS
          </h2>

          <div className="mt-8 space-y-8">
            <Sub title="O que prevê">
              <p>
                O IGNIS não prevê o dia exato de um incêndio. Calcula, para cada ponto do território,
                uma pontuação de risco de ignição e traduz essa pontuação em quatro níveis: Reduzido,
                Moderado, Elevado e Extremo.
              </p>
            </Sub>

            <Sub title="Como funciona">
              <p>
                O IGNIS aprendeu com o passado. Reunimos décadas de registos de incêndios reais e, para
                cada dia e cada lugar, comparámos as condições (o tempo, a secura, a vegetação, o terreno
                e o histórico) com o que realmente ardeu. O modelo aprendeu os padrões que costumam
                surgir antes de um fogo e aplica-os às condições previstas para os próximos dias.
              </p>
            </Sub>

            <Sub title="O que significa o nível de risco">
              <p>
                O risco é uma pontuação relativa, não uma certeza nem uma frequência garantida. Um nível Elevado não garante que
                vai arder: significa que as condições se parecem com as que, no passado, precederam muitos
                incêndios. Do mesmo modo, Reduzido não quer dizer risco zero.
              </p>
            </Sub>

            <Sub title="Como ler o mapa">
              <p>No mapa e nas consultas vais encontrar três informações:</p>
              <ul className="space-y-3">
                <li>
                  <span className="font-medium text-mist">Nível de risco</span> (Baixo, Moderado, Alto,
                  Extremo): o resumo com cores. É o mais importante.
                </li>
                <li>
                  <span className="font-medium text-mist">Pontuação</span>: um valor relativo de risco,
                  não uma frequência observada de incêndios. Como o fogo é um evento raro, valores
                  pequenos já contam: 5% já corresponde a risco moderado. Medimos publicamente a
                  relação entre a pontuação e a realidade na página Validação.
                </li>
                <li>
                  <span className="font-medium text-mist">Comparação com a época</span>: quão invulgar é o
                  dia para aquele local e altura do ano. "Invulgarmente perigoso" quer dizer que está
                  entre os dias mais perigosos de sempre para aquela época, mesmo que a pontuação
                  pareça baixa.
                </li>
              </ul>
            </Sub>

            <Sub title="Para que serve">
              <p>
                Serve para antecipar e planear: sensibilização, prontidão operacional e apoio à decisão no
                território. É um complemento aos avisos oficiais do IPMA e da Proteção Civil, e não um
                substituto. Em situação de emergência, siga sempre as fontes oficiais.
              </p>
            </Sub>
          </div>
        </div>

        {/* ============================ PART 2 ============================ */}
        <div className="mt-16 border-t border-ink-800 pt-12">
          <div className="mb-2 font-mono text-xs uppercase tracking-[0.22em] text-ember">
            Detalhe técnico
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-mist sm:text-3xl">
            Versão técnica
          </h2>
          <p className="mt-4 leading-relaxed text-mist-muted">
            Uma descrição mais rigorosa para leitores técnicos. Não substitui o artigo científico, mas
            resume o método, a avaliação e as limitações.
          </p>

          <div className="mt-8 space-y-8">
            <Sub title="Formulação do problema">
              <p>
                Classificação binária da ocorrência de ignição por célula-dia, numa grelha de 1 km sobre
                Portugal continental. A saída é uma pontuação calibrada por ordenação (na prática, um valor relativo de risco;
                a relação com as frequências observadas está medida na página Validação), agregada
                em quatro níveis de risco. Horizonte de 24 a 72 horas, com reconstrução diária.
              </p>
            </Sub>

            <Sub title="Dados e variáveis">
              <p>
                O modelo usa cerca de 63 variáveis tabulares por célula-dia, fundidas de fontes públicas:
              </p>
              <dl className="mt-2 space-y-2.5">
                {FEATURE_FAMILIES.map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-ink-700 bg-ink-900 px-4 py-3">
                    <dt className="font-mono text-[11px] uppercase tracking-wider text-ember">{k}</dt>
                    <dd className="mt-1 text-sm text-mist-muted">{v}</dd>
                  </div>
                ))}
              </dl>
            </Sub>

            <Sub title="Modelo">
              {rebuiltServed ? (
                <p>
                  O modelo reconstruído em 2026 parte de uma tabela simples, a taxa histórica de
                  ignições de cada quadrícula de 1 km em cada mês, e aprende por gradient boosting
                  (LightGBM) o desvio face a essa taxa com a meteorologia do dia (IFS, ERA5-Land,
                  índices FWI) e as características do terreno. Calibração isotónica no ano anterior.
                  Uma emissão por dia; as classes do mapa são quotas fixas do território (Extremo 1%,
                  Alto 2%, Moderado 5%), atribuídas por ordem de risco. O modelo anterior (ensemble
                  sazonal XGBoost + LightGBM, be23 e lst1) fica como série fechada na Validação.
                </p>
              ) : (
                <>
                  <p>
                    O modelo em serviço é um ensemble sazonal (XGBoost + LightGBM, internamente be23 e
                    lst1), congelado desde a primavera de 2026: escolhe o modelo mais adequado a cada
                    época do ano e calibra as probabilidades mês a mês. As classes do mapa são limiares
                    sobre essa probabilidade; o mapa de hoje renova-se de 3 em 3 horas e a previsão a 24
                    horas de 6 em 6.
                  </p>
                  <p>
                    Em 2026 reconstruímos o modelo: parte da taxa histórica de ignições de cada quadrícula
                    de 1 km em cada mês e aprende por gradient boosting (LightGBM) o desvio face a essa
                    taxa com a meteorologia do dia (IFS, ERA5-Land, índices FWI) e as características do
                    terreno, com calibração isotónica no ano anterior. Uma emissão por dia; as classes
                    passam a quotas fixas do território (Extremo 1%, Alto 2%, Moderado 5%).{' '}
                    <span className="text-mist">Corre em sombra desde {IGNIS_MODELS.rebuilt.shadowSince}</span>,
                    em paralelo com o modelo em serviço e sem o substituir, até passar o portão descrito em
                    Modelos.
                  </p>
                </>
              )}
            </Sub>

            <Sub title="Avaliação">
              <p>
                A avaliação é o centro do trabalho. Uma avaliação ingénua, com negativos fáceis (pontos
                pouco propensos a arder), produz uma AUC de cerca de 0.99, mas mede susceptibilidade
                espacial e não capacidade de previsão.
              </p>
              <p>
                A medida que usamos é outra: de todas as ignições de um ano não visto, que fração cai
                nos 5% de quadrículas que o mapa sinaliza nesse dia, contando todas as quadrículas do
                território em todos os dias. Em 2026, o modelo reconstruído cobre {fmtPct1(cov.rebuilt)} das
                ignições, a tabela histórica por quadrícula e mês cobre {fmtPct1(cov.lookup)} e o modelo{' '}
                {rebuiltServed ? 'anterior cobria' : 'em serviço cobre'} {fmtPct1(cov.served)}.
                Publicámos em 2026 uma AUC "honesta" de 0,83 para {rebuiltServed ? 'o modelo anterior' : 'este modelo'};
                ao reavaliar com negativos do mesmo mês e sem linhas do mesmo evento, esse valor cai
                para 0,50 a 0,56, e retirámos a afirmação em setembro de 2026. A comparação prospetiva,
                dia a dia, está na Validação.
              </p>
            </Sub>

            <Sub title="Resultados">
              <p>
                O que a reconstrução de 2026 mostrou: a tabela histórica por quadrícula e mês é uma
                referência difícil de bater ({fmtPct1(cov.lookup)} das ignições cobertas), o modelo{' '}
                {rebuiltServed ? 'anterior ficava' : 'em serviço fica'} abaixo dela ({fmtPct1(cov.served)})
                apesar de uma AUC publicada de 0,83, e o modelo novo, que aprende o desvio face à tabela,
                fica acima ({fmtPct1(cov.rebuilt)}). O ganho vem da meteorologia do dia sobre o
                histórico, não de mais variáveis.
              </p>
              {!rebuiltServed && (
                <p>
                  Em sombra, ao vivo, desde {IGNIS_MODELS.rebuilt.shadowSince} (estado em {STATE_DATE}):
                  nos {sh.liveDays} dias já pontuados ({fmtInt(sh.liveFires)} ignições) o modelo em serviço
                  cobriu {fmtPct1(sh.live.served)}, a tabela histórica {fmtPct1(sh.live.lookup)} e o modelo
                  reconstruído {fmtPct1(sh.live.rebuilt)}. Poucos dias ainda; o portão pede{' '}
                  {IGNIS_MODELS.rebuilt.gate.liveDays} dias e {fmtInt(IGNIS_MODELS.rebuilt.gate.fires)} ignições.
                  A contagem completa está em Modelos.
                </p>
              )}
              <p>
                Reportamos também, deliberadamente, o que não resultou: índices de vegetação por satélite,
                humidade do combustível vivo (LFMC/NDMI), camada de pressão de ignição, decomposição de
                causa, decaimento por recência e microtopografia (invisível à métrica estratificada, por
                ser estática). Estes resultados negativos delimitam o teto dos dados atuais.
              </p>
            </Sub>

            <Sub title="Posicionamento">
              <p>
                Cientificamente, o IGNIS re-aprende em grande parte o sinal do FWI, com valor incremental
                na resolução (1 km) e na fusão de fontes. Posiciona-se como complemento ao IPMA RCM e ao
                EFFIS, não como substituto nem como estado-da-arte. É esta honestidade, e não um número
                inflacionado, que sustenta a sua credibilidade.
              </p>
            </Sub>
          </div>
        </div>

        <div className="mt-16 flex flex-wrap gap-4 border-t border-ink-800 pt-8">
          <Link
            to="/ignis/mapa-nacional"
            className="inline-flex items-center gap-2 rounded-lg bg-ember px-6 py-3 font-semibold text-ink-950 shadow-[0_0_30px_-8px_#F26A4B] transition-all duration-200 hover:brightness-110"
          >
            Ver o mapa nacional
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-lg px-5 py-3 font-mono text-sm uppercase tracking-widest text-mist-muted transition-colors hover:text-mist"
          >
            Voltar a Tutela
          </Link>
        </div>
      </div>
    </div>
  );
}
